import { useState, useCallback, useMemo } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { Asset, AssetFieldDefinition, AssetStatus } from "shared/types/asset";

/**
 * useAssetBulkActions Hook
 *
 * Provides bulk action functionality for asset instances following the helpdesk pattern.
 * Handles selection state, field editing, and bulk update operations.
 *
 * @param assets - All assets (for field definition analysis)
 * @param categories - Asset categories with field definitions
 * @param onRefresh - Callback to refresh data after updates
 */
export const useAssetBulkActions = (
  assets: Asset[],
  categories: any[],
  onRefresh: () => void
) => {
  const { session } = useSession();

  // Selection state for bulk actions (Asset IDs are UUIDs/strings)
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());

  // Build map of categoryId -> field definitions
  const categoryFieldsMap = useMemo(() => {
    const map = new Map();
    categories.forEach(cat => {
      if (cat.fieldDefinitions) {
        map.set(cat.id, cat.fieldDefinitions);
      }
    });
    return map;
  }, [categories]);

  // Get editable fields - only fields that exist in ALL selected assets' categories
  // Exclude fields with file-log connections (they are read-only)
  const editableFields = useMemo(() => {
    if (selectedAssets.size === 0) return [];

    const selectedAssetsList = assets.filter(a => selectedAssets.has(a.id));
    const categoryIds = new Set(selectedAssetsList.map(a => a.assetCategoryId));

    if (categoryIds.size === 0) return [];

    // Get field definitions from first category
    const firstCategoryId = Array.from(categoryIds)[0];
    const firstCategoryFields = categoryFieldsMap.get(firstCategoryId) || [];

    // Filter to only fields that exist in all categories AND are not connected to file-logs
    return firstCategoryFields.filter((field: any) => {
      // Exclude fields with file-log connections (read-only)
      if (field.fileLogConnection?.enabled) {
        return false;
      }

      // Check if field exists in all selected categories
      return Array.from(categoryIds).every(catId => {
        const catFields = categoryFieldsMap.get(catId) || [];
        return catFields.some((f: any) => f.fieldName === field.fieldName);
      });
    });
  }, [selectedAssets, assets, categoryFieldsMap]);

  // Map field type to picker mode
  const getPickerModeForField = (field: AssetFieldDefinition): string | null => {
    switch (field.fieldType) {
      case 'date':
        return 'document-date';
      case 'enum':
      case 'multi_enum':
        return 'custom-tags';
      case 'boolean':
        return 'custom-tags';
      case 'string':
        return 'text-input';
      case 'number':
        return 'number-input';
      default:
        return null;
    }
  };

  // Handle bulk update for a specific field
  const handleBulkUpdate = useCallback(async (
    field: AssetFieldDefinition,
    value: any,
    assetIds?: string[]  // Optional: if provided, use these IDs instead of selectedAssets
  ) => {
    if (!session) return;

    // Use provided assetIds or fall back to selectedAssets
    const targetAssetIds = assetIds && assetIds.length > 0
      ? assetIds
      : Array.from(selectedAssets);

    if (targetAssetIds.length === 0) return;

    const selectedAssetIds = targetAssetIds;

    try {
      // Update each asset
      const updatePromises = selectedAssetIds.map(async (assetId) => {
        const asset = assets.find(a => a.id === assetId);
        if (!asset) return;

        // Merge new field value with existing fieldValues
        const currentFieldValues = asset.fieldValues || {};
        const updatedFieldValues = {
          ...currentFieldValues,
          [field.fieldName]: value,
        };

        const response = await fetchWithAuth(
          `${API_URL}/assets/${assetId}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fieldValues: updatedFieldValues,
            }),
          },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to update asset ${assetId}`);
        }
      });

      await Promise.all(updatePromises);

      toast.success(
        `${field.displayName} bijgewerkt voor ${selectedAssetIds.length} voorziening${selectedAssetIds.length === 1 ? '' : 'en'}`
      );

      // Only clear selection if we used the selectedAssets (bulk edit)
      // Don't clear if assetIds were explicitly provided (single edit)
      if (!assetIds || assetIds.length === 0) {
        setSelectedAssets(new Set());
      }
      onRefresh();
    } catch (error) {
      console.error('Error bulk updating assets:', error);
      toast.error('Fout bij bulk bijwerken van voorzieningen');
    }
  }, [session, selectedAssets, assets, onRefresh]);

  // Handle bulk status update (status is a top-level asset property, not a fieldValue)
  const handleBulkStatusUpdate = useCallback(async (
    newStatus: AssetStatus,
    assetIds?: string[]
  ) => {
    if (!session) return;

    const targetIds = assetIds?.length ? assetIds : Array.from(selectedAssets);
    if (targetIds.length === 0) return;

    try {
      const updatePromises = targetIds.map(async (assetId) => {
        const response = await fetchWithAuth(
          `${API_URL}/assets/${assetId}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: newStatus }),
          },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to update status for asset ${assetId}`);
        }
      });

      await Promise.all(updatePromises);

      toast.success(
        `Status bijgewerkt voor ${targetIds.length} voorziening${targetIds.length === 1 ? '' : 'en'}`
      );

      // Only clear selection if we used the selectedAssets (bulk edit)
      if (!assetIds || assetIds.length === 0) {
        setSelectedAssets(new Set());
      }
      onRefresh();
    } catch (error) {
      console.error('Error bulk updating asset status:', error);
      toast.error('Fout bij bijwerken van status');
    }
  }, [session, selectedAssets, onRefresh]);

  // Dispatch picker event (following helpdesk pattern)
  const openFieldPicker = useCallback((field: AssetFieldDefinition) => {
    const pickerMode = getPickerModeForField(field);

    if (!pickerMode) {
      toast.info(`Bulk bewerken van ${field.displayName} wordt nog niet ondersteund`);
      return;
    }

    // Dispatch event to open picker (UniversalTable listens for this)
    const event = new CustomEvent('asset-open-picker', {
      detail: {
        mode: pickerMode,
        field,
        assetIds: Array.from(selectedAssets),
        config: {
          // For boolean fields
          customOptions: field.fieldType === 'boolean'
            ? [
                { label: 'Ja', value: true },
                { label: 'Nee', value: false },
              ]
            : field.enumValues
            ? field.enumValues.map(val => ({ label: val, value: val }))
            : undefined,
          multiSelect: field.fieldType === 'multi_enum',
        }
      }
    });
    window.dispatchEvent(event);
  }, [selectedAssets]);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedAssets(new Set());
  }, []);

  // Handle bulk delete
  const handleBulkDelete = useCallback(async () => {
    if (!session) return;

    const selectedAssetIds = Array.from(selectedAssets);
    if (selectedAssetIds.length === 0) return;

    // Confirm deletion
    const confirmed = window.confirm(
      `Weet u zeker dat u ${selectedAssetIds.length} voorziening${selectedAssetIds.length === 1 ? '' : 'en'} wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`
    );

    if (!confirmed) return;

    try {
      // Delete each asset
      const deletePromises = selectedAssetIds.map(async (assetId) => {
        const response = await fetchWithAuth(
          `${API_URL}/assets/${assetId}`,
          { method: 'DELETE' },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to delete asset ${assetId}`);
        }
      });

      await Promise.all(deletePromises);

      toast.success(
        `${selectedAssetIds.length} voorziening${selectedAssetIds.length === 1 ? '' : 'en'} verwijderd`
      );

      setSelectedAssets(new Set());
      onRefresh();
    } catch (error) {
      console.error('Error bulk deleting assets:', error);
      toast.error('Fout bij verwijderen van voorzieningen');
    }
  }, [session, selectedAssets, onRefresh]);

  // Open status picker for bulk status change
  const openStatusPicker = useCallback(() => {
    const event = new CustomEvent('asset-open-picker', {
      detail: {
        mode: 'custom-tags',
        field: { fieldName: '__status__', displayName: 'Status' },
        assetIds: Array.from(selectedAssets),
        config: {
          customOptions: [
            { label: 'Actief', value: AssetStatus.ACTIVE },
            { label: 'Onderhoud', value: AssetStatus.MAINTENANCE },
            { label: 'Buiten gebruik', value: AssetStatus.DECOMMISSIONED },
            { label: 'Inactief', value: AssetStatus.INACTIVE },
            { label: 'Installatie gepland', value: AssetStatus.PENDING_INSTALLATION },
            { label: 'Defect', value: AssetStatus.DEFECT },
            { label: 'Gestopt', value: AssetStatus.RETIRED },
          ],
        },
      },
    });
    window.dispatchEvent(event);
  }, [selectedAssets]);

  // Bulk actions configuration (formatted for UniversalTable searchConfig.bulkActions)
  const getBulkActions = useCallback(() => {
    if (selectedAssets.size === 0) return [];

    // Status action always available
    const statusAction = {
      label: 'Status wijzigen',
      iconType: 'edit' as const,
      onClick: () => openStatusPicker(),
      variant: 'primaryMoss' as const,
    };

    // Create bulk action for each editable field
    const fieldActions = editableFields.map((field: any) => ({
      label: `Bewerk ${field.displayName}`,
      iconType: 'edit' as const,
      onClick: () => openFieldPicker(field),
      variant: 'primaryMoss' as const,
    }));

    // Add delete action
    const deleteAction = {
      label: `Verwijderen (${selectedAssets.size})`,
      iconType: 'trash' as const,
      onClick: () => handleBulkDelete(),
      variant: 'red' as const,
    };

    return [statusAction, ...fieldActions, deleteAction];
  }, [selectedAssets.size, editableFields, openFieldPicker, openStatusPicker, handleBulkDelete]);

  return {
    selectedAssets,
    setSelectedAssets,
    editableFields,
    handleBulkUpdate,
    handleBulkStatusUpdate,
    handleBulkDelete,
    openFieldPicker,
    clearSelection,
    getBulkActions,
    selectedCount: selectedAssets.size,
    getPickerModeForField,
  };
};
